import I18n from "i18n-js";
import React from "react";
import pathToRegexp from "path-to-regexp";
import { arrayOf, func, number, shape } from "prop-types";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import ClientTypes from "../models/ClientTypes";
import ButtonCreateSite from "../components/ButtonCreateSite";
import {
  deleteClient,
  getClientsCount,
  getClientsList,
} from "../actions/clientsActions";
import List from "../../common/components/layout/List";
import TextInput from "../../common/components/form/TextInput";
import {
  ROUTE_CLIENT,
  ROUTE_CLIENTS_ADD,
  ROUTE_CLIENTS_IMPORT,
} from "../../../routes";
import Roles from "../../users/models/Roles";
import { CompanySyncStatusesTable } from "../../trackdechets/components/CompanySyncStatusesTable";
import { getSitesList } from "../actions/sitesActions";
import DownloadButton from "../../common/components/elements/DownloadButton";
import moment from "moment";
import { fetch } from "../../../appContainer";

class ClientsList extends List {
  static allowedTypes = [ClientTypes.FINAL, ClientTypes.SERVICE_BENEFICIARY];

  static propTypes = {
    deleteClient: func.isRequired,
    clients: shape().isRequired,
    clientsList: arrayOf(shape).isRequired,
    userProfile: shape().isRequired,
    getCount: func.isRequired,
    count: number,
  };

  constructor() {
    super();

    this.clientRoute = pathToRegexp.compile(ROUTE_CLIENT);

    this.dataTableRow = this.dataTableRow.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.exportToTsv = this.exportToTsv.bind(this);
  }

  /**
   * {@inheritdoc}
   */
  componentDidMount() {
    super.componentDidMount(this.fetchData);
  }

  computeFilters(userFilters) {
    return {
      ...userFilters,
      types: ClientsList.allowedTypes,
    }
  }

  fetchData() {
    const { filters } = this.state;
    const { getCount, getClientsList, getSitesList } = this.props;

    getCount(this.computeFilters(filters));
    getClientsList(this.computeFilters(filters));
    getSitesList({ ...filters, page: undefined });
  }

  /**
   * @param {String}
   */
  onDelete(uuid) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(I18n.t("pages.clients.confirm.delete"))) {
      return;
    }

    this.props.deleteClient(uuid);
  }

  userCanImportClients() {
    const { roles, clientType } = this.props.userProfile;
    // At least MANAGER role & not a company user
    return roles.includes(Roles.MANAGER) && clientType !== ClientTypes.COMPANY;
  }

  exportToTsv() {
    const { filters } = this.state;
    return new Promise((download) => {
      fetch("api").exportClients(
        this.computeFilters(filters),
        (tsvContent) => download(tsvContent),
        (error) => {
          console.error(error);
          download(null);
        },
      );
    });
  }

  /**
   * @return {Array}
   */
  dataTableHead() {
    return [
      I18n.t("pages.clients.head.externalId"),
      I18n.t("pages.clients.head.legalCompanyName"),
      I18n.t("pages.clients.head.address"),
      I18n.t("pages.clients.head.actions"),
      I18n.t("pages.clients.head.actions"),
    ];
  }

  /**
   * @param {Object} rowData
   *
   * @return {Array}
   */
  dataTableRow(rowData) {
    const address = rowData.address;
    const addressString =
      address &&
      `${address.street}${
        address.addressAddition ? ` ${address.addressAddition}` : ""
      }, ${address.postal} ${address.city}, ${address.country}`;

    const notProvided = <small>{I18n.t("common.not_specified")}</small>;

    const actions = rowData.type === ClientTypes.FINAL && (
      <div>
        <Link to={this.clientRoute({ uuid: rowData.uuid })} className="action">
          <i className="fa-solid fa-pencil" aria-hidden="true" />
        </Link>
        <button
          onClick={() => this.onDelete(rowData.uuid)}
          className="action danger"
        >
          <i className="fa-solid fa-trash" aria-hidden="true" />
        </button>
      </div>
    );

    return [
      rowData.externalId || notProvided,
      rowData.legalCompanyName,
      addressString,
      <ButtonCreateSite key="createSite" client={rowData} />,
      actions,
    ];
  }

  /**
   * {@inheritdoc}
   */
  renderTitle() {
    return I18n.t("pages.clients.list.title");
  }

  /**
   * {@inheritdoc}
   */
  renderContent() {
    const { clients, clientsList, count, userProfile } = this.props;

    // if (clients.inProgress) {
    //   return <div className="loader" />;
    // }

    if (clients.error) {
      return <p>{I18n.t("common.loadingApiError")}</p>;
    }

    const actions = (
      <div>
        {this.userCanImportClients() && (
          <Link to={ROUTE_CLIENTS_IMPORT} className="btn btn-primary me-2">
            <i className="fa-solid fa-file" />{" "}
            {I18n.t("pages.clients.list.actions.import")}
          </Link>
        )}
        <DownloadButton
          className="me-2"
          fileContent={this.exportToTsv}
          filename={`climapp_clients_${moment().format(
            "YYYY-MM-DD_HH-mm-ss"
          )}.tsv`}
          label={I18n.t("common.actions.export")}
          contentType="text/csv;charset=utf8;"
        />
        <Link to={ROUTE_CLIENTS_ADD} className="btn btn-primary">
          <i className="fa-solid fa-plus" />{" "}
          {I18n.t("pages.clients.list.actions.add")}
        </Link>
      </div>
    );
    const filteredList = clientsList;
    // .filter(this.getFilterByText("legalCompanyName"))
    // .filter(this.getCustomFilter("city", this.cityFilter));

    return (
      <CompanySyncStatusesTable
        target="CLIENT"
        allData={clientsList || []}
        emptyContent={I18n.t("pages.my_enterprise.list.empty")}
        data={filteredList}
        headData={this.dataTableHead()}
        rowData={this.dataTableRow}
        pagination
        onPageChanged={this.onPageChanged}
        currentPage={this.getCurrentPage()}
        filters={this.renderFilters()}
        actions={actions}
        showTrackdechetsInfo={Boolean(userProfile?.usesTrackdechets)}
        dataCount={count}
        manualPagination
        inProgress={clients.inProgress}
      />
    );
  }

  /**
   * {@inheritdoc}
   */
  renderFilters() {
    const { filters } = this.state;

    return [
      <TextInput
        key="filterByLegalCompanyName"
        id="filterByLegalCompanyName"
        defaultValue={filters.legalCompanyName}
        onChange={(event) => this.onChangeFilter(event, "legalCompanyName")}
        label={I18n.t("filters.by_legalCompanyName")}
        optional
      />,
      <TextInput
        key="filterByCity"
        id="filterByCity"
        defaultValue={filters.city}
        onChange={(event) => this.onChangeFilter(event, "city")}
        label={I18n.t("filters.by_city")}
        optional
      />,
    ];
  }

  onUpdateUrl(newFilters) {
    const { getClientsList, getSitesList, getCount } = this.props;

    getClientsList(this.computeFilters(newFilters));
    getSitesList({ ...newFilters, page: undefined });
    getCount(this.computeFilters(newFilters));
  }
}

const mapStateToProps = (state) => {
  return {
    clients: state.clients,
    clientsList: state.clients.list,
    userProfile: state.auth.userProfile,
    count: state.clients.count,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getClientsList: (filters) => dispatch(getClientsList(filters)),
  getSitesList: (filters) => dispatch(getSitesList(filters)),
  deleteClient: (uuid) => dispatch(deleteClient(uuid)),
  getCount: (filters) => dispatch(getClientsCount(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientsList);
